import Head from 'next/head'
import Link from 'next/link'

import type { FC, ReactNode } from 'react'
import type { AdminUser } from '@/types'

import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { adminFetch } from '@/lib/admin/client'
import { useSettings } from '@/components/providers/settings-provider'
import { BrandMark } from '@/components/generals/logo'
import { Sheet } from '@/components/generals/sheet'
import { MODEL_TABS } from '@/constants/model3d'

import {
    ClipboardList,
    ExternalLink,
    LayoutDashboard,
    LoaderCircle,
    LogOut,
    Menu,
    Package,
    PackagePlus,
    Settings,
    Sparkles,
    Tags,
    X
} from 'lucide-react'

const MENU = [
    { href: '/admin', label: 'Ringkasan', icon: LayoutDashboard, exact: true },
    { href: '/admin/produk', label: 'Kelola Produk', icon: Package, exact: true },
    { href: '/admin/produk/baru', label: 'Tambah Produk', icon: PackagePlus, exact: true },
    { href: '/admin/kategori', label: 'Kelola Kategori', icon: Tags, exact: true },
    { href: '/admin/pesanan', label: 'Kelola Pesanan', icon: ClipboardList, exact: true },
    { href: '/admin/pengaturan', label: 'Pengaturan Toko', icon: Settings, exact: true }
]

// The AI 3D generator and its model lists (the list tabs double as sub-menu entries).
const MODEL_MENU = [
    { href: '/admin/model-3d/baru', label: 'Generator 3D AI', tab: null },
    ...MODEL_TABS.map(tab => ({ href: tab.id === 'semua' ? '/admin/model-3d' : `/admin/model-3d?status=${tab.id}`, label: tab.label, tab: tab.id }))
]

interface AdminLayoutProps {
    admin: AdminUser
    title: string
    description?: ReactNode
    actions?: ReactNode
    newOrders?: number
    children: ReactNode
}

const AdminNav: FC<{ admin: AdminUser, newOrders: number, onNavigate?: () => void }> = ({ admin, newOrders, onNavigate }): ReactNode => {
    const router = useRouter()
    const settings = useSettings()
    const [leaving, setLeaving] = useState(false)

    const isActive = (href: string) => {
        if (href === '/admin/produk') return router.pathname === '/admin/produk' || router.pathname === '/admin/produk/[id]'

        return router.pathname === href
    }

    const logout = async () => {
        setLeaving(true)

        try {
            await adminFetch('/api/admin/auth/logout', { method: 'POST' })
        }
        finally {
            window.location.assign('/admin')
        }
    }

    return (
        <div className='flex h-full flex-col'>
            <div className='flex items-center gap-3 px-5 pb-6 pt-6'>
                <BrandMark size={64} className='ring-4 ring-white shadow-[0_10px_24px_-14px_rgba(58,40,30,0.6)]' />
                <div className='min-w-0'>
                    <p className='truncate font-heading text-lg font-semibold tracking-[0.04em] text-cocoa-900'>
                        <span data-mdlfy-cms={settings.id ? `store_settings:${settings.id}:store_name` : undefined}>{settings.storeName}</span>
                    </p>
                    <p className='text-xs font-semibold text-blush-600'>
                        Dashboard Admin
                    </p>
                </div>
            </div>

            <nav aria-label='Menu admin' className='flex-1 overflow-y-auto px-3'>
                <ul className='space-y-1'>
                    {MENU.map(item => {
                        const Icon = item.icon
                        const active = isActive(item.href)

                        return (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    onClick={onNavigate}
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(
                                        'flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition',
                                        active ? 'bg-cocoa-800 text-cream-50 shadow-[0_12px_24px_-16px_rgba(58,40,30,0.9)]' : 'text-cocoa-700 hover:bg-white hover:text-cocoa-900'
                                    )}
                                >
                                    <Icon className='size-5 shrink-0' />
                                    <span className='flex-1'>{item.label}</span>
                                    {item.href === '/admin/pesanan' && newOrders > 0 && (
                                        <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-bold', active ? 'bg-blush-200 text-cocoa-900' : 'bg-blush-500 text-white')}>
                                            {newOrders}
                                        </span>
                                    )}
                                </Link>
                            </li>
                        )
                    })}
                    <li className='pt-4'>
                        <p className='px-4 pb-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-cocoa-500'>
                            Model 3D AI
                        </p>
                        <ul className='space-y-0.5'>
                            {MODEL_MENU.map(item => {
                                const status = typeof router.query.status === 'string' ? router.query.status : 'semua'
                                const active = item.tab
                                    ? (router.pathname === '/admin/model-3d' && status === item.tab) || (item.tab === 'semua' && router.pathname === '/admin/model-3d/[id]')
                                    : router.pathname === '/admin/model-3d/baru'

                                return (
                                    <li key={item.href}>
                                        <Link
                                            href={item.href}
                                            onClick={onNavigate}
                                            aria-current={active ? 'page' : undefined}
                                            className={cn(
                                                'flex items-center gap-3 rounded-2xl transition',
                                                item.tab ? 'py-2 pl-12 pr-4 text-[13px] font-semibold' : 'px-4 py-3 text-sm font-semibold',
                                                active ? 'bg-cocoa-800 text-cream-50 shadow-[0_12px_24px_-16px_rgba(58,40,30,0.9)]' : 'text-cocoa-700 hover:bg-white hover:text-cocoa-900'
                                            )}
                                        >
                                            {!item.tab && <Sparkles className='size-5 shrink-0' />}
                                            <span className='flex-1'>{item.label}</span>
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                    </li>
                    <li className='pt-3'>
                        <button
                            type='button'
                            onClick={logout}
                            disabled={leaving}
                            className='flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-semibold text-cocoa-700 transition hover:bg-white hover:text-[#9c3b34] disabled:opacity-60'
                        >
                            {leaving ? <LoaderCircle className='size-5 animate-spin' /> : <LogOut className='size-5' />}
                            Keluar
                        </button>
                    </li>
                </ul>
            </nav>

            <div className='space-y-3 border-t border-border/70 px-5 py-5'>
                <a
                    href='/'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex items-center justify-between rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-cocoa-800 shadow-sm transition hover:bg-cream-50'
                >
                    Lihat toko
                    <ExternalLink className='size-4 text-cocoa-500' />
                </a>
                <p className='truncate text-xs text-muted-foreground'>
                    Masuk sebagai <span className='font-semibold text-cocoa-700'>{admin.name}</span>
                </p>
            </div>
        </div>
    )
}

export const AdminLayout: FC<AdminLayoutProps> = ({ admin, title, description, actions, newOrders = 0, children }): ReactNode => {
    const router = useRouter()
    const settings = useSettings()
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    return (
        <>
            <Head>
                <title>{`${title} - Admin ${settings.storeName}`}</title>
                <meta name='robots' content='noindex, nofollow' />
            </Head>

            <div className='min-h-screen bg-[radial-gradient(120%_80%_at_100%_0%,#fdf1f2_0%,#fbf5ee_45%,#f8efe3_100%)]'>
                <aside className='fixed inset-y-0 left-0 z-40 hidden w-72 border-r border-border/70 bg-cream-100/80 backdrop-blur lg:block'>
                    <AdminNav admin={admin} newOrders={newOrders} />
                </aside>

                <header className='sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-border/70 bg-cream-50/90 px-4 backdrop-blur-xl lg:hidden'>
                    <Link href='/admin' className='flex items-center gap-2.5'>
                        <BrandMark size={40} />
                        <span className='font-heading text-base font-semibold tracking-[0.04em] text-cocoa-900'>
                            Admin
                        </span>
                    </Link>
                    <button
                        type='button'
                        onClick={() => setMenuOpen(true)}
                        aria-label='Buka menu admin'
                        className='grid size-11 place-items-center rounded-full text-cocoa-800 transition hover:bg-white'
                    >
                        <Menu className='size-5' />
                    </button>
                </header>

                <Sheet open={menuOpen} onClose={() => setMenuOpen(false)} labelledBy='admin-menu-title' side='left'>
                    <p id='admin-menu-title' className='sr-only'>Menu admin</p>
                    <button
                        type='button'
                        onClick={() => setMenuOpen(false)}
                        aria-label='Tutup menu'
                        className='absolute right-4 top-4 z-10 grid size-10 place-items-center rounded-full text-cocoa-800 hover:bg-muted'
                    >
                        <X className='size-5' />
                    </button>
                    <AdminNav admin={admin} newOrders={newOrders} onNavigate={() => setMenuOpen(false)} />
                </Sheet>

                <main className='lg:pl-72'>
                    <div className='mx-auto max-w-6xl px-4 pb-24 pt-6 sm:px-8 sm:pt-10'>
                        <div className='mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
                            <div>
                                <h1 className='font-heading text-3xl text-cocoa-900 font-soft sm:text-4xl'>
                                    {title}
                                </h1>
                                {description && (
                                    <p className='mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-[15px]'>
                                        {description}
                                    </p>
                                )}
                            </div>
                            {actions && <div className='flex flex-wrap gap-2'>{actions}</div>}
                        </div>
                        {children}
                    </div>
                </main>
            </div>
        </>
    )
}
